<template>
  <div>
    <div class="modal-backdrop"></div>
    <div
      class="modal task code-modal"
      :class="{ 'full-screen': isModalFullScreen }"
      @mouseup="mouseUpAtEditor()"
    >
      <div class="content" v-if="isComponentRendered">
        <div class="task-tools">
          <ul class="left-side-options">
            <li
              class="run-code"
              @click="runCode()"
              v-b-tooltip.hover
              title="Ausführen"
            >
              <a class="run-code">
                <img src="../../assets/img/start.svg" />

                <span class="accessibility">Run</span>
              </a>
            </li>
          </ul>
        </div>

        <div class="pull-right">
          <div class="fullscreen-icon">
            <i
              v-if="!isModalFullScreen"
              @click="modalFullScreen()"
              class="fas fa-expand"
            ></i>
            <i
              v-if="isModalFullScreen"
              @click="modalFullScreen()"
              class="fas fa-compress"
            ></i>
          </div>
          <div @click="close" class="boostrap-close-btn">
            <b-icon icon="x-lg" font-scale="1"></b-icon>
          </div>
        </div>

        <div class="tab">
          <div class="notes">
            <p v-show="loading && isNoteVisible && !hasError">
              Wird ausgeführt !
            </p>
          </div>

          <div class="select-language" v-show="false">
            <select v-model="task.props.language">
              <option value="py">Python</option>

              <option value="js">Javascript</option>
            </select>
          </div>
          <div
            class="editor-wrapper dragable-wrapper read-only"
            ref="topTextArea"
            id="codeEditor1"
          >
            <span
              class="dragable-line-first"
              @mousedown="mouseDownAtEditor($event, 'codeEditor1')"
            ></span>
            <prism-editor
              ref="editor-read-only-input"
              class="code-editor"
              v-model="inputAsCode"
              :highlight="highlighter"
              :readonly="true"
              line-numbers
            ></prism-editor>
          </div>
          <div
            class="editor-wrapper read-write"
            ref="middleTextArea"
            id="codeEditor2"
          >
            <prism-editor
              ref="editor"
              class="code-editor"
              v-model="task.props.code"
              :highlight="highlighter"
              :readonly="false"
              line-numbers
            ></prism-editor>
          </div>
          <div
            ref="bottomTextArea"
            class="editor-wrapper read-only"
            :class="{ 'hide-overflow': loading }"
            id="codeEditor3"
          >
            <span
              class="dragable-line-second"
              @mousedown="mouseDownAtEditor($event, 'codeEditor3')"
            ></span>
            <prism-editor
              ref="editor-read-only-output"
              class="code-editor"
              v-model="outputAsCode"
              :highlight="highlighter"
              :readonly="true"
              line-numbers
            ></prism-editor>

            <!-- Use this loading, while run code is running  -->

            <div class="loading" v-show="loading">
              <div class="progress-icon-wrapper">
                <div class="progress-icon"></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Use this loading, while component is rendering  -->

      <div v-else class="loading full-screen-modal">
        <div class="pull-right">
          <div class="btn-close" @click="close()"></div>
        </div>

        <div class="progress-icon-wrapper full-screen-modal">
          Wird geladen...
          <div class="progress-icon full-screen-modal"></div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import codeEditor from '../widgets/codeEditor.vue';

// import Prism Editor
import { PrismEditor } from 'vue-prism-editor';
import 'vue-prism-editor/dist/prismeditor.min.css'; // import the styles somewhere

// import highlighting library (you can use any library you want just return html string)
import { highlight, languages } from 'prismjs/components/prism-core';

// import syntax highlighting styles
import 'prismjs/components/prism-clike';
import 'prismjs/components/prism-javascript';
import 'prismjs/components/prism-python';
import 'prismjs/themes/prism-tomorrow.css';

export default {
  name: 'Code',
  data() {
    return {
      referenceCode: null,
      loading: false,
      isNoteVisible: false,
      hasError: false,
      isComponentRendered: false,
      jsonTypes: ['object', 'objectlist'],
      modelTypes: [
        'timeseries', // timeseries is needed for backwards compatiblility
        'loadprofile',
        'custom',
      ],
      listTypes: ['checkbox'],
      isModalFullScreen: false,
      currentEditor: null,
      m_pos: null,
    };
  },
  props: {
    task: Object,
    process: Object,
  },
  methods: {
    close: function () {
      this.$set(this.task, 'output', this.task.output.concat(this.task.input));
      this.$emit('close');
    },
    runCode: function () {
      this.loading = true;
      this.referenceCode = this.task.props.code;

      // As flowrunner expects only first task, remove other tasks
      // and clear output
      let f = JSON.parse(JSON.stringify(this.process));
      f.tasks = [f.tasks[0]];
      f.tasks[0].output = [];

      // Put props and input in form data
      var formData = new FormData();
      formData.append('flow', JSON.stringify(f));
      formData.append('props', JSON.stringify(this.task.props));

      var myHeaders = new Headers();
      myHeaders.append(
        'Authorization',
        'Bearer ' + this.user.signInUserSession.accessToken.jwtToken
      );

      // Put input in form data
      if (this.task.input) {
        this.task.input.forEach((e) => {
          if (e.type == 'file') formData.append(e.name, e.value);
          else if (this.jsonTypes.includes(e.type))
            formData.append(e.name, JSON.stringify(e.value));
          else if (this.modelTypes.includes(e.type))
            formData.append(e.name, e.value.id);
          else if (this.listTypes.includes(e.type)) {
            e.value.forEach((el) => formData.append(e.name, el));
          } else formData.append(e.name, e.value);
        });
      }

      // Call API
      var url = this.flowRunnerEndpoint;
      url += '/run_code';

      fetch(url, {
        body: formData,
        headers: myHeaders,
        method: 'post',
      })
        .then(async (response) => {
          if (response.status == 500) {
            let jsn = await response.json().then((jsn) => {
              return jsn;
            });
            throw new Error(jsn['message']);
          }
          if (response.status != 200) throw new Error(response.statusText);
          else return response.json();
        })
        .then((json) => {
          this.task.output = json;

          if (json.filter((e) => e.label == 'error').length > 0) {
            this.hasError = true;
            this.showToaster(
              'Bitte beheben Sie den Fehler im Code !',
              'Fehlerhaft !',
              'danger'
            );
            this.showNote();
            this.$set(this.task, 'status', 'error');
          } else {
            this.hasError = false;
            this.$set(this.task, 'status', 'success');
            this.$emit('resetErrorMessage');
          }

          this.loading = false;
        })
        .catch((e) => {
          this.task.output = [
            {
              label: 'error',
              name: 'error',
              type: 'text',
              value: e,
            },
          ];

          this.hasError = true;
          this.showToaster(
            'Bitte beheben Sie den Fehler im Code !',
            'Fehlerhaft !',
            'danger'
          );
          this.loading = false;
        });
    },
    highlighter(code) {
      return highlight(code, languages.py);
    },
    showNote: function () {
      this.isNoteVisible = true;
      setTimeout(() => {
        this.isNoteVisible = false;
      }, 2000);
    },
    getTSDisplayFormat: function (ts) {
      // Make deepcopy
      let copy = JSON.parse(JSON.stringify(ts));

      // Modify data for display purpose
      let d = copy.data.slice(0, 2);
      d.push(['...'], ['...']);
      copy.data = d;

      return copy;
    },
    isObjectTimeseries: function (o) {
      let a = o.hasOwnProperty('ts_type');
      let b = o.hasOwnProperty('supplyPoint');
      let c = o.hasOwnProperty('quantity');
      let d = o.hasOwnProperty('convention');

      return a + b + c + d;
    },
    mouseDownAtEditor(e, editor) {
      e.preventDefault();
      this.currentEditor = editor;
      this.m_pos = e.y;
      document.addEventListener('mousemove', this.resizeCodeEditor, false);
    },
    textAreaHeight() {
      return {
        topPanel: this.$refs.topTextArea.getBoundingClientRect().height,
        middlePanel: this.$refs.middleTextArea.getBoundingClientRect().height,
        bottomPanel: this.$refs.bottomTextArea.getBoundingClientRect().height,
      };
    },
    resizeCodeEditor(e) {
      const panelsHeight = this.textAreaHeight();
      window.addEventListener('mouseup', this.mouseUpAtEditor, true);
      this.m_pos = e.y;

      //Code for the top code textarea to resize
      if (this.currentEditor === 'codeEditor1') {
        const topPanelNewHeight = panelsHeight.topPanel + e.movementY;
        const middlePanelNewHeight = panelsHeight.middlePanel - e.movementY;
        const bottomPanelNewHeight = panelsHeight.bottomPanel - e.movementY;
        //if condition when middle textarea is bigger than top area
        if (topPanelNewHeight > 2 && middlePanelNewHeight > 150) {
          this.$refs.topTextArea.style.height = topPanelNewHeight + 'px';

          this.$refs.middleTextArea.style.height = middlePanelNewHeight + 'px';
        }

        // if condition when bottom textarea is bigger than top and middel area
        if (middlePanelNewHeight < 155 && bottomPanelNewHeight > 150) {
          this.$refs.topTextArea.style.height = topPanelNewHeight + 'px';

          this.$refs.bottomTextArea.style.height = bottomPanelNewHeight + 'px';
        }
      }

      //Code for the bottom code textarea to resize
      if (this.currentEditor === 'codeEditor3') {
        const topPanelNewHeight = panelsHeight.topPanel + e.movementY;
        const bottomPanelNewHeight = panelsHeight.bottomPanel - e.movementY;
        const middlePanelNewHeight = panelsHeight.middlePanel + e.movementY;

        //if condition when middle textarea is bigger than bottom area
        if (bottomPanelNewHeight > 2 && middlePanelNewHeight > 150) {
          this.$refs.bottomTextArea.style.height = bottomPanelNewHeight + 'px';

          this.$refs.middleTextArea.style.height = middlePanelNewHeight + 'px';
        }

        //if condition when top textarea is bigger than bottom and middel area
        if (topPanelNewHeight > 150 && middlePanelNewHeight < 155) {
          this.$refs.topTextArea.style.height = topPanelNewHeight + 'px';

          this.$refs.bottomTextArea.style.height = bottomPanelNewHeight + 'px';
        }
      }
    },

    mouseUpAtEditor() {
      //only call removeEventListener for mousemove when mouseup event fire
      document.removeEventListener('mousemove', this.resizeCodeEditor, false);
      window.removeEventListener('mouseup', this.mouseUpAtEditor, true);
    },
    modalFullScreen() {
      this.isModalFullScreen = !this.isModalFullScreen;
    },
    showToaster(message, title, variant) {
      this.$bvToast.toast(message, {
        title,
        variant,
      });
    },
  },
  created() {
    this.task.output = [];
  },
  computed: {
    inputAsCode() {
      let code =
        '""" We initiated this dictionary for you based on the input from the previous task. You can not edit this part of the code, but you can use this dictionary in your code."""\n\n';

      if (this.isComponentRendered) {
        if (this.task.input) {
          this.task.input.forEach((e) => {
            let name = e.name;
            switch (e.type) {
              case 'text':
              case 'email':
              case 'hidden':
              case 'textarea':
              case 'model':
              case 'date':
              case 'radio':
                if (name != 'codelosSettings')
                  code += 'input["' + name + '"] = "' + e.value + '"\n';
                break;
              case 'formatted_number':
              case 'number':
                code += 'input["' + name + '"] = ' + e.value + '\n';
                break;
              // Remove timeseries when all flows updated
              // Show only 2 values of data, otherwise building the component takes too long
              case 'timeseries':
              case 'loadprofile':
                code +=
                  'input["' +
                  name +
                  '"] = ' +
                  JSON.stringify(this.getTSDisplayFormat(e.value)) +
                  '\n';
                break;
              case 'objectlist':
              case 'object':
              case 'custom':
              case 'checkbox':
                code +=
                  'input["' + name + '"] = ' + JSON.stringify(e.value) + '\n';
                break;
              case 'file':
                var fileName;
                if (e.value.name) fileName = e.value.name;
                else fileName = e.name;
                code +=
                  'input["' +
                  e.name +
                  '"] = {"file_path": "/tmp/' +
                  fileName +
                  '"}\n';
                break;
            }
          });
        }
      } else {
        // Use timeout, to keep reactivity of computed "inputAsCode"
        // eslint-disable-next-line vue/no-async-in-computed-properties
        setTimeout(() => {
          // eslint-disable-next-line vue/no-side-effects-in-computed-properties
          this.isComponentRendered = true;
        }, 5);
      }

      return code;
    },
    outputAsCode() {
      let code = '# Results:\n\n';
      this.task.output.forEach((e) => {
        let name = e.name;
        switch (e.type) {
          case 'text':
          case 'email':
          case 'date':
            if (name != 'codelosSettings')
              code += 'results["' + name + '"] = "' + e.value + '"\n';
            break;
          case 'formatted_number':
          case 'number':
            code += 'results["' + name + '"] = "' + e.value + '"\n';
            break;
          case 'file': {
            let path = e.value.file_path;
            if (path == 'undefined') path = e.name;
            code +=
              'results["' +
              name +
              '"]' +
              ' = {"file": (' +
              name +
              ', open("' +
              path.split('/')[2].split('.')[0] +
              '", "rb"), "' +
              path.split('/')[2].split('.')[1] +
              '")}\n';
            break;
          }
          case 'object':
          case 'list':
            if (this.isObjectTimeseries(e.value)) {
              // Show only 2 values of data, otherwise building the component takes too long
              code +=
                'input["' +
                name +
                '"] = ' +
                JSON.stringify(this.getTSDisplayFormat(e.value)) +
                '\n';
              break;
            } else
              code +=
                'results["' + name + '"] = ' + JSON.stringify(e.value) + '\n';
            break;
          case 'error':
            code +=
              '# This error occured while executing the code: \n"' +
              e.value +
              '"\n';
            break;
          default:
            break;
        }
      });
      return code;
    },
    user() {
      return this.$store.state.user;
    },
    flowRunnerEndpoint() {
      if (
        this.process.settings.hasOwnProperty('apiEndpoint') &&
        this.process.settings.apiEndpoint != ''
      ) {
        return this.process.settings.apiEndpoint;
      } else return process.env.VUE_APP_FLOW_RUNNER_API;
    },
  },
  mounted() {
    setTimeout(() => {
      this.isComponentRendered = true;
    }, 5);
  },
  directives: {
    clickOutside: {
      bind: function (el, binding, vnode) {
        el.eventSetDrag = function () {
          el.setAttribute('data-dragging', 'yes');
        };
        el.eventClearDrag = function () {
          el.removeAttribute('data-dragging');
        };
        el.eventOnClick = function (event) {
          var dragging = el.getAttribute('data-dragging');
          // Check that the click was outside the el and its children, and wasn't a drag
          if (!(el == event.target || el.contains(event.target)) && !dragging) {
            // call method provided in attribute value
            vnode.context[binding.expression](event);
          }
        };
        document.addEventListener('touchstart', el.eventClearDrag);
        document.addEventListener('touchmove', el.eventSetDrag);
        document.addEventListener('click', el.eventOnClick);
        document.addEventListener('touchend', el.eventOnClick);
      },
      unbind: function (el) {
        document.removeEventListener('touchstart', el.eventClearDrag);
        document.removeEventListener('touchmove', el.eventSetDrag);
        document.removeEventListener('click', el.eventOnClick);
        document.removeEventListener('touchend', el.eventOnClick);
        el.removeAttribute('data-dragging');
      },
    },
  },
  components: {
    PrismEditor,

    // Do not delete codeEditor, its not used but everything will change if we remove it.
    // eslint-disable-next-line vue/no-unused-components
    codeEditor,
  },
};
</script>

<style scoped>
.modal-backdrop {
  position: fixed;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  background-color: rgba(0, 0, 0, 0.3);
  display: flex;
  justify-content: center;
  align-items: center;
}
.heading {
  padding: 15px 15px 0px;
  text-align: center;
}

.content {
  background-color: #fff;
  width: 100%;
  -webkit-box-sizing: border-box;
  box-sizing: border-box;
  text-align: center;
}

.tab {
  text-align: left;
  padding: 20px 31px;
  height: 100%;
}

.tab .notes {
  float: right;
  margin-top: 20px;
}
.tab .notes p {
  color: rgb(247, 57, 89);
  font-size: 13px;
}

/* code-editor */
.dragable-wrapper {
  position: relative;
}
.dragable-line-first {
  position: absolute;
  display: inline-block;
  width: 100%;
  height: 5px;
  background-color: #fff;
  bottom: 0;
  left: 0;
  right: 0;
  opacity: 0.5;
  cursor: row-resize;
  z-index: 99;
  user-select: none;
}

.dragable-line-second {
  position: absolute;
  display: inline-block;
  width: 100%;
  height: 5px;
  background-color: #fff;
  opacity: 0.5;
  cursor: row-resize;
  z-index: 9;
}
.editor-wrapper.read-only {
  height: 150px;
}

.editor-wrapper {
  margin-top: 25px;
  height: 350px;
  margin-left: auto;
  margin-right: auto;
  overflow-y: scroll;
  width: 100%;
  border-radius: 4px;
}

.editor-wrapper.read-write .prism-editor-wrapper.code-editor {
  min-height: 450px;
  height: 100%;
  padding-top: 0;
}

::v-deep .prism-editor__container {
  height: fit-content;
  min-height: 450px;
}

.select-language {
  margin-top: 50px;
  width: 100%;
  text-align: right;
}

.select-language select {
  width: 110px;
  height: 42px;
  font-size: 18px;
  border: none;
  background: transparent;
}

.modal {
  background: #ffffff;
  border-radius: 4px;
  display: block;
  width: 80%;
  height: 98%;
  text-align: left;
  font-family: 'Open Sans', open-sans, sans-serif;
  -webkit-font-smoothing: antialiased;
  font-size: 12px;
  line-height: 17px;
  color: #5f697a;
  top: 0;
  position: absolute;
  left: 10%;
  top: 1%;
}

.heading {
  padding: 15px 15px 0px;
  text-align: center;
}

h1 {
  padding-top: 50px;
  padding-bottom: 8px;
  text-align: center;
  font-size: 33px;
  color: #464d5d;
  margin: 0 0 12px;
  font-weight: 400;
  line-height: 35px;
  text-rendering: optimizelegibility;
  -webkit-font-smoothing: antialiased;
}

h3 {
  color: rgba(85, 93, 109, 0.4);
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  margin-bottom: 10px;
}

h4 {
  color: rgba(85, 93, 109, 0.4);
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  margin-bottom: 0px;
}

input {
  float: left;
  background: #fff;
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
  border-right: medium none;
  padding-left: 12px;
  padding-bottom: 15px;
  margin-bottom: 0;
  height: 51px;
  padding-bottom: 14px;
  padding-top: 15px;
  color: #464d5d;
  border: 1px solid #dee3e9;
  border-radius: 2px;
  font-size: 14px;
  font-weight: 400;
  line-height: 19px;
  margin: 0 0 20px;
  -webkit-font-smoothing: antialiased;
  -webkit-box-shadow: none;
  box-shadow: none;
  -webkit-box-sizing: border-box;
  box-sizing: border-box;
}

textarea {
  float: left;
  background: #fff;
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
  border-right: medium none;
  padding-left: 12px;
  padding-bottom: 15px;
  margin-bottom: 0;
  padding-bottom: 14px;
  padding-top: 15px;
  color: #464d5d;
  border: 1px solid #dee3e9;
  border-radius: 2px;
  font-size: 14px;
  font-weight: 400;
  line-height: 19px;
  margin: 0 0 20px;
  -webkit-font-smoothing: antialiased;
  -webkit-box-shadow: none;
  box-shadow: none;
  -webkit-box-sizing: border-box;
  box-sizing: border-box;
  height: 200px;
  width: 100%;
  overflow: scroll;
}

button {
  border: 1px solid #dee3e9;
  border-radius: 3px 3px 3px 3px;
  width: 30%;
  height: 51px;
  text-transform: uppercase;
  font-weight: 600;
}

button.default {
  color: #464d5d;
  background: #fff;
}

.input-group {
  width: 100%;
}

.imput-group > input {
  width: 70%;
}

.input-group > button {
  min-width: 0;
  width: 30%;
  height: 51px;
  text-transform: uppercase;
  font-weight: 600;
  color: #fff;
  background: #6bbc7c;
  border: 1px solid #5ab36c;
  margin-bottom: 0;
  border-radius: 0 3px 3px 0;
}

.pull-right {
  float: right;
}

.modal .btn-close {
  margin-left: 14px;
  border: none;
  display: block;
  width: 20px;
  top: 0;
  height: 14px;
  margin-top: 0;
  opacity: 1;
  box-sizing: border-box;
}

button.btn-green {
  color: white;
  background: #4aae9b;
  border: 1px solid #4aae9b;
  border-radius: 2px;
  position: relative;
}

ul.tabsLabels {
  display: inline-block;
  padding: 0;
  margin: 0 0 -1px;
}

.modal ul.tabsLabels li {
  display: inline-block;
  margin: 0 18px;
  list-style: none;
  padding: 0;
  line-height: 14px;
}

.modal:not(.custom) li {
  font-size: 14px;
  line-height: 25px;
  color: #7e8890;
}

.modal ul.tabsLabels a {
  color: #a4abb1;
  font-size: 12px;
  font-weight: 400;
  text-transform: uppercase;
  padding: 21px 7px 22px 33px;
  border-bottom: 2px solid transparent;
  display: block;
  position: relative;
}

a {
  text-decoration: none;
  -webkit-transition: color 0.2s ease, background-color 0.2s ease,
    border-color 0.2s ease;
  -o-transition: color 0.2s ease, background-color 0.2s ease,
    border-color 0.2s ease;
  transition: color 0.2s ease, background-color 0.2s ease,
    border-color 0.2s ease;
  cursor: pointer;
}

a {
  background-color: transparent;
}

.modal ul.tabsLabels a.active {
  border-color: #52b266;
}

.modal ul.tabsLabels a.active,
.modal ul.tabsLabels a:hover {
  color: #464d5d;
  text-decoration: none;
}

/*@media not all, not all, (-webkit-min-device-pixel-ratio: 1.5), not all, (min-resolution: 1.5dppx)*/
.tabsLabels .url {
  background-image: url('../../assets/img/modal-sprite.png');
  background-size: 1293px 1277px;
  background-position: -893px -1109px;
}

.tabsLabels .email {
  background-image: url('../../assets/img/modal-sprite.png');
  background-size: 1293px 1277px;
  background-position: -1100px -1110px;
}

.tabsLabels .mscons {
  background-image: url('../../assets/img/modal-sprite.png');
  background-size: 1293px 1277px;
  background-position: -828px -1175px;
}

.tabsLabels .system {
  background-image: url('../../assets/img/modal-sprite.png');
  background-size: 1293px 1277px;
  background-position: -137px -1178px;
}

p {
  font-weight: 500;
  font-size: 12px;
  line-height: 21px;
  color: #7e8890;
  margin: 25px 0px 25px 0px;
}

.systems {
  background-color: #f9f9fb;
  font-family: 'Open Sans', open-sans, sans-serif;
  -webkit-font-smoothing: antialiased;
  font-size: 12px;
  color: #5f697a;
  line-height: 17px;
  margin: 0;
  display: block;
  overflow: auto;
  width: 100%;
}

.item-wrapper {
  display: block;
  position: relative;
  float: left;
  width: 120px;
  border-radius: 4px;
  background: #fff;
  height: 150px;
  text-align: center;
  margin: 0 39px 43px 0;
  -webkit-box-shadow: 0 1px 2px rgba(43, 59, 93, 0.29);
  box-shadow: 0 1px 2px rgba(43, 59, 93, 0.29);
  -webkit-box-sizing: border-box;
  box-sizing: border-box;
}

.item-wrapper h2 {
  color: #424956;
  font-size: 12px;
  font-weight: 400;
  line-height: inherit;
  margin-top: 0px;
}

.systems-wrapper {
  width: 100%;
  margin: 24px 31px;
}

.img-wrapper {
  width: 100%;
  height: 111px;
}

.systems .logo {
  position: absolute;
  width: 70%;
  position: relative;
  top: 50%;
  transform: translateY(-50%);
}

.pull-right.default {
  margin-top: 0;
}

.e-mail-params {
  height: 176px;
}

button {
  margin-top: 20px;
}

.form-group {
  width: 100%;
  height: 50px;
  display: flex;
  margin-top: 0px;
  padding-top: 1px;
  padding-bottom: 1px;
  border-bottom: 1px;
  border-bottom-style: solid;
  border-bottom-color: #dee0e4;
}

.form-group .label {
  margin-top: 20px;
  text-transform: none;
}

.form-group input {
  width: 100%;
  border: 0;
  padding-bottom: 14px;
  padding-top: 18px;
}

input:focus {
  outline: none;
}

.tools {
  left: 594px;
  position: relative;
  display: flex;
  -webkit-box-orient: vertical;
  -webkit-box-direction: normal;
  -ms-flex-direction: column;
  flex-direction: column;
  -webkit-box-pack: center;
  -ms-flex-pack: center;
  justify-content: center;
  -webkit-box-align: center;
  -ms-flex-align: center;
  align-items: center;
  width: 46px;
  height: 199px;
}

.element {
  margin-bottom: 16px;
}

.tools .element .button {
  background-color: var(--color-black-lighter);
  fill: #fff;
  stroke: #fff;
  width: 46px;
  height: 46px;
  border-radius: var(--circled);
  border: 0;
  -webkit-box-shadow: var(--elevation-medium);
  box-shadow: var(--elevation-low);
  transform: translateZ(0);
  cursor: pointer;
  outline: 0;
  display: flex;
  -webkit-box-pack: center;
  justify-content: center;
  -webkit-box-align: center;
  align-items: center;
  padding: 0;
  transition: all 0.3s ease-out;
}

.tools .element .button:hover {
  fill: var(--color-black-lighter);
  stroke: var(--color-black-lighter);
  background: #fff;
  -webkit-box-shadow: var(--elevation-high);
  box-shadow: var(--elevation-high);
}

.task-tools {
  float: left;
}

.task-tools i {
  margin-top: 5px;
  margin-left: 3px;
  color: black;
  font-size: 20px;
}

.pause-code i {
  margin-top: 6px;
  margin-left: 0px;
}

.left-side-options {
  margin-top: 28px;
  padding-left: 19px;
}

.left-side-options ul {
  display: block;
  list-style-type: none;
  margin-block-start: 1em;
  margin-block-end: 1em;
  margin-inline-start: 0px;
  margin-inline-end: 0px;
  padding-inline-start: 40px;
}

.left-side-options li {
  display: inline-block;
  margin-left: 10px;
}

.task-tool .left-side-options {
  position: absolute;
  width: auto;
  text-align: right;
}

.task-tool .left-side-options {
  display: block;
  list-style-type: none;
  margin-block-start: 1em;
  margin-block-end: 1em;
  margin-inline-start: 0px;
  margin-inline-end: 0px;
  padding-inline-start: 40px;
}

.left-side-options > li.download .run-code:hover {
  background: rgba(0, 0, 0, 0.03);
  overflow: hidden;
  display: block;
  transition: color 0.15s;
  cursor: pointer;
}

.left-side-options > li.download .run-code {
  box-sizing: border-box;
  border: 1px solid #dbdde0;
  border-radius: 50%;
  height: 35px;
  width: 35px;
  top: 11px;
  left: 11px;
  background: #fff;
  overflow: hidden;
  display: block;
  transition: color 0.15s;
}

.left-side-options > li > a:hover {
  background: rgba(0, 0, 0, 0.03);
  overflow: hidden;
  display: block;
  transition: color 0.15s;
  cursor: pointer;
}

.left-side-options > li > a {
  box-sizing: border-box;
  border: 1px solid #dbdde0;
  border-radius: 50%;
  height: 45px;
  width: 45px;
  top: 11px;
  left: 11px;
  background: #fff;
  overflow: hidden;
  display: block;
  transition: color 0.15s;
  padding-top: 9px;
  padding-left: 2px;
}

.accessibility {
  z-index: 30;
  overflow: hidden !important;
  padding: 0 !important;
  position: absolute !important;
  width: 0px !important;
}

.button--loading::after {
  content: '';
  position: absolute;
  width: 35px;
  height: 35px;
  right: 43px;
  bottom: 5px;
  margin: auto;
  border: 2px solid transparent;
  border-top-color: #181818;
  border-radius: 50%;
  animation: button-loading-spinner 1s ease infinite;
}

@keyframes button-loading-spinner {
  from {
    transform: rotate(0turn);
  }

  to {
    transform: rotate(1turn);
  }
}

.paper-clip {
  padding-left: 5px;
  padding-top: 5px;
}

.data-fields {
  margin-top: 10px;
  margin-left: 1px;
}

.action {
  margin-left: -5px;
  margin-top: 24px;
}

:root {
  --color-pink: #f36;
  --color-pink-darker: #e81e61;
  --color-blue-lighter: #f5fbff;
  --color-blue: #276ee5;
  --color-blue-darker: #1f53d5;
  --color-green-lighter: #f2fbfa;
  --color-green: #09baa6;
  --color-green-darker: #00a08e;
  --color-yellow-lighter: #fffbf4;
  --color-yellow: #ffb631;
  --color-yellow-darker: #e8a01e;
  --color-red-lighter: #fdf3f5;
  --color-red: #e01e3d;
  --color-red-darker: #c60d2b;
  --color-ink-lightest: #9599a2;
  --color-ink-lighter: #6e7077;
  --color-ink: #22252b;
  --color-ink-darker: #17191d;
  --color-gray-lightest: #f8f8fa;
  --color-gray-lighter: #ebecee;
  --color-gray: #dee0e4;
  --color-gray-darker: #c7cad1;
  --color-black-lighter: #182237;
  --color-black: #15181f;
  --color-white: #fff;
  --elevation-low: 0 0 0 1px rgba(0, 0, 0, 0.05), 0 1px 2px rgba(0, 0, 0, 0.12);
  --elevation-medium: 0 0 0 1px rgba(0, 0, 0, 0.05),
    0 5px 10px rgba(0, 0, 0, 0.15);
  --elevation-high: 0 0 0 1px rgba(0, 0, 0, 0.05),
    0 10px 20px rgba(0, 0, 0, 0.15);
  --rounded: 4px;
  --circled: 50%;
  --pilled: 64px;
}

.tools .element:hover .info-wrapper {
  display: block;
}

.info-wrapper {
  position: absolute;
  display: none;
  will-change: opacity, visibility;
  transition: visibility 0.2s cubic-bezier(0.2, 0.91, 0.85, 0.96) 0.35s,
    opacity 0.2s cubic-bezier(0.2, 0.91, 0.85, 0.96) 0.35s;
  margin-top: -38px;
  right: 59px;
}

.info-wrapper .info {
  font-family: 'Inv Maison Neue', 'Maison Neue', -apple-system,
    BlinkMacSystemFont, 'Open Sans', open-sans, sans-serif;
  font-size: 12px;
  font-weight: 300;
  line-height: 16px;
  letter-spacing: 0.125px;
  white-space: nowrap;
  color: white;
  background: rgb(23, 25, 29);
  border-radius: 4px;
  padding: 8px 16px;
}

.toggle-list-wrapper {
  background-color: #ffff;
  display: flex;
  position: absolute;
  right: 59px;
  -webkit-box-shadow: var(--elevation-high);
  box-shadow: var(--elevation-high);
  max-width: 600px;
  overflow-y: scroll;
  z-index: 1;
  margin-top: -93px;
}

.toggle-list-wrapper.data-fields {
  border-radius: 3px;
  display: inline-block;
  clear: both;
  padding: 17px 0 17px 0;
  width: 226px;
  margin-top: 0px;
  margin-left: -186px;
  overflow-y: scroll;
  max-height: 250px;
}

.toggle-list-wrapper.actions {
  border-radius: 3px;
  display: inline-block;
  clear: both;
  padding: 17px 0 17px 0;
  width: 226px;
  margin-top: 0px;
  margin-left: -185px;
  overflow-y: scroll;
  max-height: 250px;
}

.toggle-list-wrapper .item {
  height: 135px;
  cursor: pointer;
  border-right: 1px solid #dbdee5;
  padding: 8px;
  width: 130px;
  text-align: center;
}

.toggle-list-wrapper.data-fields .item,
.toggle-list-wrapper.actions .item {
  width: 100%;
  text-align: left;
  height: -webkit-fit-content;
  height: -moz-fit-content;
  height: fit-content;
  font-weight: 300;
  margin: 0;
  padding: 0;
}

.toggle-list-wrapper.data-fields .item:hover,
.toggle-list-wrapper.actions .item:hover {
  background: #f0f0f0;
}

.toggle-list-wrapper.data-fields .item .label,
.toggle-list-wrapper.actions .item .label {
  font-weight: 300;
  color: #464d5d;
  margin: 0;
}

.toggle-list-wrapper .item .label {
  overflow: hidden;
  font-size: 12px;
  text-transform: none;
  padding: 0;
  margin: 0;
  margin-top: 11px;
  height: 44px;
  padding-left: 14px;
  padding-right: 14px;
  color: #596377;
}

.pretty {
  line-height: 36px;
}

.logo {
  width: 35px;
  margin-top: 24px;
}

.check-box {
  border-style: solid;
  height: 16px;
  width: 16px;
  position: relative;
  top: -127px;
  border-width: 1px;
  border-color: var(--color-black);
  left: 1px;
  background-color: #ffff;
  transition: all 0.3s ease-out;
  border-radius: 50%;
}

.check-box .icon {
  fill: #ffff;
  zoom: 0.6;
  padding-top: 6px;
  padding-left: 3px;
}

.item.checked .check-box {
  background-color: var(--color-black);
}

.search {
  width: 200px;
  padding: 11px 0 11px 0;
  height: 21px;
  margin-left: 12px;
  border-bottom: solid 1px #bdc3c7;
  margin-right: 12px;
  margin-bottom: 12px;
}

.search-icon {
  background-image: url(data:image/png;base64,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);
  background-size: 23px;
  overflow: hidden;
  display: block;
  width: 24px;
  height: 21px;
  float: left;
  background-position: 0px 0px;
}

.search input {
  border: 0;
  width: fit-content;
  height: 13px;
  cursor: initial;
  padding-top: 11px;
  font-size: 13px;
  -webkit-font-smoothing: antialiased;
  font-weight: 300;
}

.attachment {
  overflow-y: scroll;
  margin-right: 0;
  display: block;
  width: 300px;
  top: -217px;
  position: relative;
  height: 147px;
  margin-left: 0;
  float: right;
  text-align: right;
  cursor: auto;
}

.attachment .item {
  height: 135px;
  cursor: auto;
  padding: 8px;
  width: 75px;
  text-align: center;
  display: inline-block;
  vertical-align: top;
}

.attachment .item .label {
  font-size: 12px;
  text-transform: none;
  padding: 0;
  margin: 0;
  margin-top: 11px;
  height: 44px;
  color: #596377;
}

.loading {
  width: 100%;
  height: 100%;
  background-color: #2d2d2d;
  position: relative;
  top: -100%;
  z-index: 3;
  opacity: 0.8;
  color: #ffff;
}

.loading.transparent {
  background-color: #9191912e;
}

.progress-icon-wrapper {
  top: 0;
  bottom: 0;
  height: fit-content;
  width: fit-content;
  left: 0;
  right: 0;
  margin: auto;
  position: absolute;
}

.progress-icon {
  width: 76px;
  height: 76px;
  margin-left: auto;
  top: 0;
  left: 0;
  margin-right: auto;
  margin-top: 0;
}

.progress-icon:after {
  content: ' ';
  display: block;
  width: 70px;
  height: 70px;
  margin: 1px;
  border-radius: 50%;
  border: 2px solid #ffff;
  border-color: #ffff transparent #ffff transparent;
  animation: progress-icon 1.2s linear infinite;
}

.loading.full-screen-modal {
  width: 100%;
  height: 100%;
  background-color: #ffff;
  position: initial;
  top: -100%;
  z-index: 3;
  opacity: 1;
}

.loading.transparent.full-screen-modal {
  background-color: #9191912e;
}

.progress-icon-wrapper.full-screen-modal {
  top: 0;
  bottom: 67px;
  height: fit-content;
  width: fit-content;
  left: 0;
  right: 0;
  margin: auto;
  position: absolute;
  color: rgb(90, 90, 90);
}

.progress-icon.full-screen-modal {
  width: 76px;
  height: 76px;
  margin-left: auto;
  top: 0;
  left: 0;
  margin-right: auto;
  margin-top: 28px;
}

.progress-icon.full-screen-modal:after {
  content: ' ';
  display: block;
  width: 70px;
  height: 70px;
  margin: 1px;
  border-radius: 50%;
  border: 2px solid #313745;
  border-color: #313745 transparent #313745 transparent;
  animation: progress-icon 1.2s linear infinite;
}

@keyframes progress-icon {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.modal.task.full-screen {
  width: 100%;
  height: 100%;
  top: 0;
  box-shadow: none;
  border-radius: 0;
}

.code-modal .pull-right {
  display: flex;
  align-items: center;
  margin-top: 28px;
  font-size: 17px;
  color: #000;
  opacity: 0.5;
  padding-right: 38px;
}
.code-modal .pull-right i {
  cursor: pointer;
}
#codeEditor1 .prism-editor-wrapper.code-editor {
  padding-top: 0;
}

/***** */
.code-modal .content {
  height: 94%;
}
.code-modal .content .tab {
  display: flex;
  flex-direction: column;
}

div#codeEditor2 {
  flex: 1 1 auto;
}
::v-deep .toast .toast-header button.close {
  margin-left: auto;
  margin-bottom: 0;
  font-size: 1.3rem;
  line-height: 0;
  color: #000;
  opacity: 0.5;
  border: none;
  background: transparent;
}
::v-deep .toast:not(.show) {
  display: block;
}
.editor-wrapper.hide-overflow {
  overflow-y: hidden;
}

.boostrap-close-btn {
  cursor: pointer;
  display: inline-block;
}
.boostrap-close-btn svg {
  position: relative;
  /* top: 1px; */
  margin-left: 14px;
}
</style>
